import * as React from 'react';
import { CalendarItemsConfig } from '../interfaces/CalendarItemsConfig';
import { SurfaceDecorator, SurfaceEventHandlers } from '../interfaces/SurfaceEventHandlers';

/**
 * Wraps provided content into component for injecting decorator and events hadlers
 * {React.CSSProperties} style - the css style to inject
 * {any} key - key to use for component
 * {SurfaceEventHandlers} eventHandlers - Event handlers to inject
 * {SurfaceDecorator<T>} decorator - The decorator function for wrapped component
 * {T} model - the model object where {T} is a type of model
 * inputRef - the cllback for providing reference to decorator element
 */
export function wrapToGridItem<T>(
    style: React.CSSProperties,
    key: React.Key,
    itemConfig: CalendarItemsConfig<T>,
    model: T,
    content: JSX.Element,
    itemIndex?: number,
    tabId?: string,
    hasConflicts?: boolean
): JSX.Element {
    let eventHandlers = itemConfig?.itemsEventHandlers;
    if (eventHandlers) {
        style = { ...style, ...{ cursor: 'pointer' } };
    }
    return (
        <GridItem
            style={style}
            key={key}
            eventHandlers={eventHandlers}
            decorator={itemConfig?.itemsDecorator}
            target={model}
            itemIndex={itemIndex}
            tabId={tabId}
            hasConflicts={!!hasConflicts}>
            {content}
        </GridItem>
    );
}

/**
 * Wraps grid item containing the overflow indicator with a div to ensure
 * that outer div takes the position similar to other grid items while
 * only the grid item containing the overflow indicator is clickable.
 *
 * {React.CSSProperties} wrapperStyle - to position the outside div
 * {React.CSSProperties} gridItemStyle - to center align grid item wrt outer div
 * {any} key - key to use for component
 * {itemConfig} The items configuration
 * {T} model - the model object where {T} is a type of model
 * inputRef - the callback for providing reference to decorator element
 */
export function wrapOverflowIndicatorToGridItem<T>(
    wrapperStyle: React.CSSProperties,
    gridItemStyle: React.CSSProperties,
    key: React.Key,
    itemConfig: CalendarItemsConfig<T>,
    model: T,
    content: JSX.Element,
    itemIndex?: number,
    tabId?: string
): JSX.Element {
    return (
        <div style={wrapperStyle} key={key}>
            <GridItem
                style={gridItemStyle}
                key={key}
                eventHandlers={itemConfig ? itemConfig.itemsEventHandlers : null}
                decorator={itemConfig?.itemsDecorator}
                target={model}
                itemIndex={itemIndex}
                tabId={tabId}>
                {content}
            </GridItem>
        </div>
    );
}

/**
 * GridItem properties
 * {React.CSSProperties} style - the css style to inject
 * {SurfaceEventHandlers} eventHandlers - Event handlers to inject
 * {SurfaceDecorator<T>} decorator - The decorator function for wrapped component
 * {T} target - the model object where {T} is a type of model
 * inputRef - the cllback for providing reference to decorator element
 */
export interface GridItemProps<T> {
    style: React.CSSProperties;
    eventHandlers: SurfaceEventHandlers;
    decorator?: SurfaceDecorator<T>;
    target: T;
    itemIndex: number;
    tabId: string;
    inputRef?: (element: HTMLDivElement) => void;
    hasConflicts?: boolean; // this determines if the item conflicts with any other visible event and will be needed in case it does for ItemPeek to rendered covering the target or not
    children?: React.ReactNode;
}

/**
 * This component injects decorators and event hadlers for wrapped component
 */
function GridItem<T>(props: GridItemProps<T>) {
    const boundEventHandlers = React.useMemo(() => {
        const boundEventHandlers = {};
        if (props.eventHandlers) {
            Object.keys(props.eventHandlers).forEach(key => {
                if (props.eventHandlers[key]) {
                    boundEventHandlers[key] = props.eventHandlers[key].bind(props.target);
                }
            });
        }
        return boundEventHandlers;
    }, [props.eventHandlers, props.target]);

    let DecoratorElement = props.decorator ?? 'div';
    return (
        <DecoratorElement
            item={props.target}
            data-itemindex={props.itemIndex}
            data-tabid={props.tabId}
            data-conflict={props.hasConflicts ? 1 : 0}
            style={props.style}
            {...boundEventHandlers}
            ref={props.inputRef}>
            {props.children}
        </DecoratorElement>
    );
}
